import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import ParticipantProfileModal from '@components/UserProfileModal/ParticipantProfileModal';
import SharingWindow from '../SharingWindow';
import GameCast from './GameCast/GameCast';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '../../providers';
import { useConference } from './Dolby';
import { useChatState } from '@core/chat/context';
import VideoPlayerModal from '@core/dashboard/components/VideoPlayerModal';
import { useStyles } from './styles';
import CoreWeave from './CoreWeave/Coreweave';
import { StreamingProviders } from '../../providers/MytaverseEventProvider/interfaces';
import DevelopersTerminal from '../DevelopersTerminal';
import {
  getClientSpecificFeedbackModal,
  getClientSpecificTooltipGuide,
} from '@helpers/clientSpecificUtils';
import { env } from 'env';
import FeedbackForm, {
  feedbackFormData,
  officeHoursFeedbackFormData,
} from '@core/dashboard/components/FeedbackForm';
import FilesService from '@services/FilesService';
import { AnalyticsType } from '@interfaces/analytics';
import { getNotification } from '@components/Notification/helpers';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { useTranslation } from 'react-i18next';
import { IUser } from '@interfaces/user';
import {
  shouldShowFeedbackForEvent,
  FeedbackFormEvents,
} from '@core/dashboard/utils/feedbackFormUtils';
import { useEventContext } from '@providers/EventProvider';
import { useMandatoryLogoutModal } from '@core/dashboard/components/ModalCountDown';
import { CustomSpotsEditor } from '@core/dashboard/components/DashboardContent/CustomSpotsEditor';

const ASK_FOR_FEEDBACK_TIMEOUT = 900000;
const MAX_TIMEOUT_SECONDS = 28800; //8h

const {
  REACT_APP_DISABLE_DEVELOPERS_TERMINAL,
  REACT_APP_CLIENT_ENV,
  REACT_APP_PAGE_TITLE = 'Metaplexus Hub',
} = env;

const DashboardContentView = () => {
  const { appTheme } = useMytaverse();

  const [open, setOpen] = useState(false);
  const {
    currentRoom,
    sharingWindowFullsceen,
    streamingProvider,
    openDevelopersTerminal,
    setOpenDevelopersTerminal,
    playVideoUrl,
    setPlayVideoUrl,
    currentParticipant,
    currentEvent,
    showCustomSpotsEditor,
    setShowCustomSpotsEditor,
  } = useMytaverseEvent();
  const {
    previewingParticipant,
    setPreviewingParticipant,
    screenMediaStreams,
  } = useConference();
  const {
    sendMessageToUnreal,
    trackAnalytics,
    showFeedbackForm,
    setShowFeedbackForm,
    user,
    setUser,
  } = useMytaverse();
  const { open: isOpenChat } = useChatState();
  const { showNotification, hideNotification } = useNotification();
  const { t: translate } = useTranslation();
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isEventRun } = useEventContext();
  const showLogoutCta = useMandatoryLogoutModal();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStepChange = (step: number) => {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAnswerChange = (questionId: string, value: any) => {};

  const handleSubmit = async (answers: Record<string, any>) => {
    await FilesService.saveFormData({
      name: `feedback-${currentParticipant?.participantId}`,
      formData: JSON.stringify(answers),
      eventId: currentEvent?.eventId,
    });

    if (REACT_APP_CLIENT_ENV === 'EK') {
      const currentFormData = currentEvent?.eventTags?.some((tag) =>
        tag.toLowerCase().includes('officehours'),
      )
        ? officeHoursFeedbackFormData
        : feedbackFormData;

      const questionsWithAnalytics = currentFormData.steps.filter(
        (question) => question.analyticsKey,
      );

      questionsWithAnalytics.forEach((question) => {
        if (question.analyticsKey) {
          trackAnalytics(question.analyticsKey, {
            name: question.id,
            value: answers[question.id],
            question: question.title,
            eventId: currentEvent?.eventId,
          });
        }
      });

      trackAnalytics(AnalyticsType.FeedbackSurveyCompleted, {
        eventId: currentEvent?.eventId,
      });
    }

    setUser((prevUser: IUser) => ({ ...prevUser, feedbackCompleted: true }));

    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
  };

  const handleClose = () => {
    setShowFeedbackForm(false);
  };

  useEffect(() => {
    if (
      user.feedbackCompleted ||
      !shouldShowFeedbackForEvent(currentEvent?.eventTags)
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      const notificationId = showNotification(
        getNotification({
          title: translate('notifications.feedbackSurvey.title', {
            platform:
              appTheme.loginPage.typography.title || REACT_APP_PAGE_TITLE,
          }),
          message: '',
          type: NOTIFICATION_TYPES.INFO,
          closeManually: true,
          withDismissButton: true,
          withReloadButton: true,
          dismissButtonText: translate(
            'notifications.feedbackSurvey.dismissButtonText',
          ),
          reloadPageText: translate(
            'notifications.feedbackSurvey.reloadPageText',
          ),
          reloadPageAction: () => {
            hideNotification(notificationId);
            setShowFeedbackForm(true);
          },
          dismissButtonAction: () => {
            hideNotification(notificationId);
          },
        }),
      );
    }, ASK_FOR_FEEDBACK_TIMEOUT);

    notificationTimeoutRef.current = timeout;

    return () => {
      clearTimeout(timeout);
      notificationTimeoutRef.current = null;
    };
  }, [
    showNotification,
    hideNotification,
    translate,
    setShowFeedbackForm,
    user,
    currentEvent?.eventTags,
  ]);

  const handleDevelopersTerminal = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.shiftKey && event.keyCode === 85) {
      setOpenDevelopersTerminal((prev) => !prev);
      event.preventDefault();
    }
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setInterval> | null = null;

    if (isEventRun && currentEvent?.endTime) {
      const now = Math.floor(Date.now() / 1000);
      const timeToEndSec = currentEvent?.endTime - now;

      if (timeToEndSec > MAX_TIMEOUT_SECONDS) {
        return;
      }

      timeoutId = setTimeout(() => {
        showLogoutCta(60);
      }, timeToEndSec * 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isEventRun, currentEvent?.endTime]);

  useEffect(() => {
    if (REACT_APP_DISABLE_DEVELOPERS_TERMINAL !== 'true') {
      window.addEventListener('keydown', handleDevelopersTerminal);

      return () => {
        window.removeEventListener('keydown', handleDevelopersTerminal);
      };
    }
  }, []);

  useEffect(() => {
    if (previewingParticipant) {
      setOpen(true);
    }
  }, [previewingParticipant]);

  useEffect(() => {
    if (currentEvent?.eventTags) {
      localStorage.setItem(
        'currentEventTags',
        JSON.stringify(currentEvent.eventTags),
      );
    } else {
      localStorage.removeItem('currentEventTags');
    }
  }, [currentEvent?.eventTags]);

  const classes = useStyles(
    sharingWindowFullsceen,
    !!screenMediaStreams?.length,
    isOpenChat,
    streamingProvider,
  );

  const isFirstRender = useRef(true);

  const handleClickEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      sendMessageToUnreal({ action: 'ESCAPE_CLICK' });
    }
  };

  useEffect(() => {
    if (!currentRoom) {
      return;
    }

    window.addEventListener('keydown', handleClickEscape);

    return () => {
      window.removeEventListener('keydown', handleClickEscape);
    };
  }, [currentRoom]);

  // useEffect(() => {
  //   if (isConferenceInitialized || currentRoom) {
  //     isFirstRender.current = false;
  //   }
  // }, [isConferenceInitialized]);

  const renderStreamingProvider = () => {
    switch (streamingProvider) {
      case StreamingProviders.CoreWeave:
        return <CoreWeave />;
      case StreamingProviders.GameCast:
      default:
        return <GameCast />;
    }
  };

  const getFeedbackFormData = () => {
    if (
      currentEvent?.eventTags?.some(
        (tag) =>
          tag.toLowerCase() ===
            FeedbackFormEvents.OfficeHoursLinux.toLowerCase() ||
          tag.toLowerCase() === FeedbackFormEvents.OfficeHours.toLowerCase(),
      )
    ) {
      return officeHoursFeedbackFormData;
    }
    return feedbackFormData;
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        transition: 'background-color 2s linear',
        backgroundColor: !isFirstRender.current ? 'black' : 'transparent',
      }}
    >
      {showFeedbackForm &&
      shouldShowFeedbackForEvent(currentEvent?.eventTags) ? (
        <FeedbackForm
          data={getFeedbackFormData()}
          onStepChange={handleStepChange}
          onAnswerChange={handleAnswerChange}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      ) : null}
      <Box sx={classes.root}>
        {getClientSpecificFeedbackModal()}
        {getClientSpecificTooltipGuide()}
        {screenMediaStreams.length ? <SharingWindow /> : null}
        {screenMediaStreams?.length ? <SharingWindow /> : null}
        <>
          {openDevelopersTerminal &&
            createPortal(<DevelopersTerminal />, document.body)}
          {renderStreamingProvider()}
        </>
        {playVideoUrl ? (
          <VideoPlayerModal
            onClose={() => {
              setPlayVideoUrl(null);
            }}
            videoUrl={playVideoUrl}
          />
        ) : null}
        <CustomSpotsEditor
          open={showCustomSpotsEditor}
          onClose={() => setShowCustomSpotsEditor(false)}
        />
        {open && (
          <ParticipantProfileModal
            isOpen={open}
            onClose={() => {
              setOpen(false);
              setPreviewingParticipant(null);
            }}
            previewingParticipant={previewingParticipant}
          />
        )}
      </Box>
    </Box>
  );
};

export default DashboardContentView;
